import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTablePreferences } from "./utils/useTablePreferences";
import DropDownComponent from "../components/formComponents/DropDownComponent";
import "../styles/ListPage.css";
import "../styles/AssignmentList.css";

const BUDDY_BASE = "https://buddy.myskillverse.ai/assignment.html";

const AssignmentList = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const { rowsPerPage, setRowsPerPage, sortConfig, setSortConfig } = useTablePreferences("table_prefs_assignments");
  const [searchTerm, setSearchTerm] = useState("");
  const [filterOptions, setFilterOptions] = useState({});
  const [filters, setFilters] = useState({ title: "", duration: "", description: "", batch: "", time: "", questions: "" });
  const [loading, setLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [importFailModal, setImportFailModal] = useState(false);
  const [importFailCount, setImportFailCount] = useState(0);
  const [importSuccess, setImportSuccess] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const filterChangedRef = useRef(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const res = await fetch("/api/filter", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ filterscreen: "assignments" }),
        });
        const data = await res.json();
        const formatted = {};
        for (let key in data) {
          formatted[key] = data[key].filter((item) => item !== "" && item !== null && item !== undefined).map((item) => ({ label: item, value: item }));
        }
        setFilterOptions(formatted);
      } catch (err) { console.error("Error fetching filter options:", err); }
    };
    fetchFilterOptions();
  }, []);

  const fetchAssignments = useCallback(async () => {
    setLoading(true);
    const queryParams = new URLSearchParams();
    queryParams.append("page", page + 1);
    queryParams.append("pageSize", rowsPerPage);
    if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());

    const filterKeyMap = { title: "assignmentTitle", duration: "assignmentDuration", description: "assignmentDescription", batch: "batchTitle", time: "assignmentTime", questions: "questionsCount" };
    Object.entries(filters).forEach(([key, value]) => {
      const v = typeof value === "string" ? value.trim() : String(value).trim();
      if (v && v !== "undefined" && v !== "null") queryParams.append(filterKeyMap[key], v);
    });
    if (sortConfig.column && sortConfig.direction) {
      queryParams.append("sortBy", sortConfig.column);
      queryParams.append("order", sortConfig.direction);
    }

    try {
      const res = await fetch(`${API_URL}/api/assignments?${queryParams.toString()}`);
      const response = await res.json();
      setAssignments(response.data || []);
      setTotalCount(response.pagination?.totalItems || 0);
    } catch (err) { console.error("Fetch Error:", err); }
    setLoading(false);
  }, [page, rowsPerPage, searchTerm, filters, sortConfig]);

  useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

  useEffect(() => {
    if (filterChangedRef.current) { setPage(0); filterChangedRef.current = false; }
  }, [filters, searchTerm]);

  const handleFilterChange = (key, value) => {
    filterChangedRef.current = true;
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const onSort = (column) => {
    const columnMap = { title: "assignmentTitle", batch: "batchTitle", duration: "assignmentDuration", description: "assignmentDescription", time: "assignmentTime", questions: "questionsCount" };
    const backendColumn = columnMap[column];
    if (!backendColumn) return;
    setPage(0);
    setSortConfig((prev) => {
      if (prev.column === backendColumn) {
        if (prev.direction === "asc") return { column: backendColumn, direction: "desc" };
        if (prev.direction === "desc") return { column: null, direction: null };
      }
      return { column: backendColumn, direction: "asc" };
    });
  };

  const thClass = (col) => sortConfig.column === col
    ? (sortConfig.direction === "asc" ? "lp-sort-asc" : "lp-sort-desc")
    : "";

  const totalPages = Math.ceil(totalCount / rowsPerPage);
  const handleRowClick = (id) => navigate(`/assignments/${id}`);
  const handleAddAssignment = () => navigate("/assignments/add");

  const handleCopyLink = (e, id) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${BUDDY_BASE}?id=${id}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const handleExportCSV = async () => {
    const queryParams = new URLSearchParams();
    const filterKeyMap = { title: "assignmentTitle", duration: "assignmentDuration", description: "assignmentDescription", batch: "batchTitle", time: "assignmentTime", Attachment: "assignmentAttachment", questions: "questionsCount" };
    if (searchTerm.trim()) queryParams.append("search", searchTerm.trim());
    Object.entries(filters).forEach(([key, value]) => {
      const v = (value ?? "").toString().trim();
      if (v) { if (key === "questions" && isNaN(Number(v))) return; queryParams.append(filterKeyMap[key], v); }
    });
    try {
      const response = await fetch(`${API_URL}/api/assignments-export?${queryParams.toString()}`, { headers: { Accept: "text/csv" } });
      if (!response.ok) throw new Error("Failed to export");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      link.href = url; link.download = `assignments-${timestamp}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) { console.error("CSV download error:", error); }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const EXPECTED_HEADERS = ["assignmentTitle","assignmentDuration","assignmentDescription","batchTitle","assignmentTime","assignmentAttachment","questionId","status"];
    const reader = new FileReader();
    reader.onload = async (event) => {
      const lines = event.target.result.split("\n").map((l) => l.trim());
      const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
      const isValid = headers.length === EXPECTED_HEADERS.length && headers.every((h, i) => h.toLowerCase() === EXPECTED_HEADERS[i].toLowerCase());
      if (!isValid) { setImportFailCount(lines.length - 1); setImportFailModal(true); return; }
      const formData = new FormData();
      formData.append("file", file);
      try {
        const res = await fetch("/api/assignments-import", { method: "POST", body: formData });
        const response = await res.json();
        const dataLines = lines.slice(1).filter((line) => line.trim().length > 0);
        const failed = dataLines.length - (response.insertedCount || 0);
        if (response.success && (response.insertedCount || 0) > 0) { setImportSuccess(true); fetchAssignments(); setTimeout(() => setImportSuccess(false), 3000); }
        if (failed > 0) { setImportFailCount(failed); setImportFailModal(true); }
      } catch (err) { console.error("Import Error:", err); setImportFailCount(lines.length - 1); setImportFailModal(true); }
      finally { e.target.value = null; }
    };
    reader.readAsText(file);
  };

  return (
    <div className="lp-page">
      {importSuccess && <div className="lp-toast">Assignment import successful</div>}

      {importFailModal && (
        <div className="lp-overlay">
          <div className="lp-modal">
            <h3>Import Failed</h3>
            <p>{importFailCount} row(s) could not be imported. Please download the sample CSV and try again.</p>
            <button className="lp-modal-btn" onClick={() => setImportFailModal(false)}>Close</button>
          </div>
        </div>
      )}

      {showImportModal && (
        <div className="lp-overlay" onClick={() => setShowImportModal(false)}>
          <div className="lp-drop-modal" onClick={(e) => e.stopPropagation()}>
            <div
              className={`lp-drop-zone${dragOver ? " lp-drag-active" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault(); setDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file) { handleCSVImport({ target: { files: [file] } }); setShowImportModal(false); }
              }}
            >
              <h3>Upload CSV File</h3>
              <p>Drag a file here, or click Browse to select one.</p>
              <div className="lp-drop-actions">
                <button className="lp-btn-primary" onClick={() => fileInputRef.current?.click()}>Browse</button>
                <button className="lp-btn-secondary" onClick={() => {
                  const link = document.createElement("a");
                  link.href = "/sample-assignment.csv"; link.download = "sample-assignment.csv";
                  document.body.appendChild(link); link.click(); link.remove();
                }}>Download Sample</button>
              </div>
              <input type="file" accept=".csv" ref={fileInputRef} style={{ display: "none" }} onChange={(e) => { handleCSVImport(e); setShowImportModal(false); }} />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="lp-header">
        <div className="lp-header-left">
          <h2 className="lp-title">All Assignments</h2>
          <span className="lp-count">{totalCount} records</span>
        </div>
        <div className="lp-header-actions">
          <button className="lp-btn-primary" onClick={handleAddAssignment}>+ Add Assignment</button>
          <button className="lp-btn-secondary" onClick={handleExportCSV}>Export CSV</button>
          <button className="lp-btn-secondary" onClick={() => setShowImportModal(true)}>Import CSV</button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="lp-toolbar">
        <div className="lp-search-wrap">
          <span className="lp-search-icon">&#128269;</span>
          <input
            type="text"
            placeholder="Search assignments…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="lp-search"
          />
        </div>
        <div className="lp-filters">
          <DropDownComponent options={filterOptions.assignmentTitle || []} value={filters.title} onChange={(val) => handleFilterChange("title", val)} placeholder="Assignment" />
          <DropDownComponent options={filterOptions.batchTitle || []} value={filters.batch} onChange={(val) => handleFilterChange("batch", val)} placeholder="Batch" />
          <DropDownComponent options={filterOptions.assignmentDuration || []} value={filters.duration} onChange={(val) => handleFilterChange("duration", val)} placeholder="Duration" />
          <DropDownComponent options={filterOptions.assignmentTime || []} value={filters.time} onChange={(val) => handleFilterChange("time", val)} placeholder="Time" />
        </div>
      </div>

      {/* Table */}
      <div className="lp-table-card">
        {loading ? (
          <div className="lp-state"><div className="lp-spinner" /><p>Loading assignments…</p></div>
        ) : assignments.length === 0 ? (
          <div className="lp-state"><p>No assignments found.</p></div>
        ) : (
          <table className="lp-table">
            <thead>
              <tr>
                <th className={thClass("assignmentTitle")} onClick={() => onSort("title")}>Assignment</th>
                <th className={thClass("batchTitle")} onClick={() => onSort("batch")}>Batch</th>
                <th className={thClass("assignmentDuration")} onClick={() => onSort("duration")}>Duration</th>
                <th className={thClass("assignmentDescription")} onClick={() => onSort("description")}>Description</th>
                <th className={thClass("assignmentTime")} onClick={() => onSort("time")}>Time</th>
                <th className={thClass("questionsCount")} onClick={() => onSort("questions")}>Questions</th>
                <th style={{ width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a) => (
                <tr key={a._id} onClick={() => handleRowClick(a._id)}>
                  <td>{a.assignmentTitle}</td>
                  <td>{a.batchTitle || "—"}</td>
                  <td>{Array.isArray(a.assignmentDuration) ? a.assignmentDuration.join(", ") : a.assignmentDuration}</td>
                  <td>{a.assignmentDescription || "—"}</td>
                  <td>{a.assignmentTime || "—"}</td>
                  <td>{a.questionsCount?.length ?? 0}</td>
                  <td onClick={(e) => e.stopPropagation()} style={{ textAlign: "center" }}>
                    <div className="al-copy-wrap">
                      <button
                        className="al-link-btn"
                        title="Copy assignment link"
                        onClick={(e) => handleCopyLink(e, a._id)}
                      >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                        </svg>
                      </button>
                      {copiedId === a._id && <span className="al-copy-tip">Copied!</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {!loading && assignments.length > 0 && (
        <div className="lp-pagination">
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.max(p - 1, 0))} disabled={page === 0}>← Previous</button>
          <span className="lp-page-info">Page {page + 1} of {totalPages}</span>
          <button className="lp-page-btn" onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))} disabled={page >= totalPages - 1}>Next →</button>
          <div className="lp-page-size">
            <label htmlFor="al-pageSize">Rows:</label>
            <select id="al-pageSize" value={rowsPerPage} onChange={(e) => { setPage(0); setRowsPerPage(Number(e.target.value)); }}>
              {[10,20,30,50,75,100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssignmentList;
